import { filesize as _filesize } from "filesize";
import dayjs from "./dayjs-local";

export function filesize(value) {
  return _filesize(value);
}

/**
 * DATE DISPLAY FILTER CONVENTIONS
 * ================================
 *
 * PREFERRED FUNCTIONS (use these for new code):
 *
 * For DATETIME fields (order_date, created_at, updated_at):
 *   - dateLocal() - converts UTC to local, format: MM/DD/YYYY
 *   - dateLocalShort() - converts UTC to local, format: MM/DD/YY
 *   - dateLocalMedium() - converts UTC to local, format: MMM D, YYYY
 *
 * For DATE-ONLY fields (delivery_date, payment_due_date, due_date):
 *   - dateRaw() - no conversion, format: MM/DD/YYYY
 *   - dateRawShort() - no conversion, format: MM/DD/YY
 *   - dateRawMedium() - no conversion, format: MMM D, YYYY
 *
 * Why this matters:
 *   A date stored as "2024-01-15" displayed with timezone conversion
 *   could show as "01/14/24" for users in western timezones.
 *
 */

// ============================================================================
// PREFERRED DATE FUNCTIONS - Use these for new code
// ============================================================================

/**
 * A calendar date (e.g. order_date/due_date) carries no time or timezone, so it must
 * never be UTC->local converted — doing so shifts the day for non-UTC viewers. It arrives
 * as a bare "2025-06-30" from model casts, but as "2025-06-30 00:00:00" (or a midnight ISO
 * string) from raw list/report queries. All three forms represent a calendar day.
 */
function isCalendarDate(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}([ T]00:00:00(\.0+)?Z?)?$/.test(value);
}

/**
 * Format date with UTC to local timezone conversion - MM/DD/YYYY
 * Use for: datetime fields (created_at, updated_at). Bare calendar dates are rendered as-is.
 */
export function dateLocal(value) {
  if (isCalendarDate(value)) return dayjs(value, "YYYY-MM-DD").format("MM/DD/YYYY");
  return dayjs.utc(value).local().format("MM/DD/YYYY");
}

/**
 * Format date with UTC to local timezone conversion - MM/DD/YY
 * Use for: datetime fields (created_at, updated_at). Bare calendar dates are rendered as-is.
 */
export function dateLocalShort(value) {
  if (isCalendarDate(value)) return dayjs(value, "YYYY-MM-DD").format("MM/DD/YY");
  return dayjs.utc(value).local().format("MM/DD/YY");
}

/**
 * Format date without timezone conversion - MM/DD/YYYY
 * Use for: date-only fields (delivery_date, payment_due_date, due_date)
 */
export function dateRaw(value) {
  return dayjs(value, "YYYY-MM-DD").format("MM/DD/YYYY");
}

/**
 * Format date without timezone conversion - MM/DD/YY
 * Use for: date-only fields (delivery_date, payment_due_date, due_date)
 */
export function dateRawShort(value) {
  return dayjs(value, "YYYY-MM-DD").format("MM/DD/YY");
}

/**
 * Format date with UTC to local timezone conversion - MMM D, YYYY
 * Use for: datetime fields (created_at, updated_at). Bare calendar dates are rendered as-is.
 */
export function dateLocalMedium(value) {
  if (isCalendarDate(value)) return dayjs(value, "YYYY-MM-DD").format("MMM D, YYYY");
  return dayjs.utc(value).local().format("MMM D, YYYY");
}

/**
 * Format date without timezone conversion - MMM D, YYYY
 * Use for: date-only fields when medium format needed
 */
export function dateRawMedium(value) {
  return dayjs(value, "YYYY-MM-DD").format("MMM D, YYYY");
}

// ============================================================================
// BACKWARD COMPATIBILITY ALIASES - For legacy code on other branches
// ============================================================================

/**
 * @deprecated Use dateLocalShort instead
 * Backward compatibility alias for code on beta branch
 */
export function dateLocalizedShort(value) {
  return dateLocalShort(value);
}

// ============================================================================
// LEGACY DATE FUNCTIONS - Keep for specific use cases
// ============================================================================

export function dateEpochBased(value) {
  return dayjs(value).utc().format("MMMM DD, YYYY");
}

/**
 * Format the given date as a time.
 */
export function time(value) {
  return dayjs(value).format("h:mm A");
}

export function timeLocalized(value) {
  return dayjs(value).format("h:mm A");
}

/**
 * Format the given date (Vue datetime friendly).
 */
export function datepicker(value) {
  return dayjs(value).format();
}

/**
 * Format the given date as a timestamp.
 */
export function datetime(value) {
  return dayjs(value).utc().format("MMMM D, YYYY h:mm A");
}

export function datetimeLocalized(value) {
  return dayjs(value).format("MMMM D, YYYY h:mm A");
}

export function datetimeLocalizedShort(value) {
  return dayjs(value).format("MM/DD/YY, h:mm A");
}

export function datetimeLocalizedShortUnix(value) {
  return dayjs.unix(value).format("MM/DD/YY, h:mm A");
}

export function bushelDateInputLocalizedFilter(value) {
  return dayjs(value).format("YYYY-MM-DD");
}

export function datetimeLocalizedAtTime(value) {
  return dayjs(value).format("MM/DD/YY @ h:mm A");
}

export function datetimeLocalizedJustTime(value) {
  return dayjs(value).format("h:mm A");
}

/**
 * Format and Return only Month, Day, and year functions
 */
export function monthFilter(value) {
  return dayjs(value).utc().format("MMMM");
}

export function monthYearFilter(value) {
  return dayjs(value).utc().format("MMM/YY");
}

export function monthFilterLocalized(value) {
  return dayjs(value).format("MMMM");
}

export function shortMonthFilter(value) {
  return dayjs(value).utc().format("MMM");
}

export function dayFilter(value) {
  return dayjs(value).utc().format("D");
}

export function dayFilterLocalized(value) {
  return dayjs(value).format("D");
}

export function yearFilter(value) {
  return dayjs(value).utc().format("YYYY");
}

export function yearFilterLocalized(value) {
  return dayjs(value).format("YYYY");
}

/**
 * Format the given date into a relative time.
 * This does not need a localized Version
 */
export function relative(value) {
  return dayjs(value).utc().local().fromNow();
}

/**
 * Format the given date into a calendar time, like relative but goes to 7/10/2011
 * format after a week of time
 */
export function calendartime(value) {
  return dayjs(value).locale("en-short").calendar(null, {
    lastDay: "[Yesterday at] LT",
    sameDay: "[Today at] LT",
    nextDay: "[Tomorrow at] LT",
    lastWeek: "[last] dddd [at] LT",
    nextWeek: "dddd [at] LT",
    sameElse: "LL",
  });
}

export function calendartimeLocalized(value) {
  return dayjs(value).locale("en-short").calendar(null, {
    lastDay: "[Yesterday at] LT",
    sameDay: "[Today at] LT",
    nextDay: "[Tomorrow at] LT",
    lastWeek: "[last] dddd [at] LT",
    nextWeek: "dddd [at] LT",
    sameElse: "LL",
  });
}

export function setupDateTimeLocallyWithTimezoneAttached(value) {
  const dt = dayjs(value, "YYYY-MM-DD h:mm A").toDate();
  const timezoneOffsetMin = new Date().getTimezoneOffset();
  let offsetHrs = parseInt(Math.abs(timezoneOffsetMin / 60), 10);
  let offsetMin = Math.abs(timezoneOffsetMin % 60);
  let timezoneStandard;

  if (offsetHrs < 10) offsetHrs = `0${offsetHrs}`;
  if (offsetMin < 10) offsetMin = `0${offsetMin}`;

  // Add an opposite sign to the offset
  // If offset is 0, it means timezone is UTC
  if (timezoneOffsetMin < 0) timezoneStandard = `+${offsetHrs}:${offsetMin}`;
  else if (timezoneOffsetMin > 0) timezoneStandard = `-${offsetHrs}:${offsetMin}`;
  else if (timezoneOffsetMin == 0) timezoneStandard = "Z";

  let currentDate = dt.getDate();
  let currentMonth = dt.getMonth() + 1;
  const currentYear = dt.getFullYear();
  let currentHrs = dt.getHours();
  let currentMins = dt.getMinutes();
  let currentSecs = dt.getSeconds();

  // Add 0 before date, month, hrs, mins or secs if they are less than 0
  currentDate = currentDate < 10 ? `0${currentDate}` : currentDate;
  currentMonth = currentMonth < 10 ? `0${currentMonth}` : currentMonth;
  currentHrs = currentHrs < 10 ? `0${currentHrs}` : currentHrs;
  currentMins = currentMins < 10 ? `0${currentMins}` : currentMins;
  currentSecs = currentSecs < 10 ? `0${currentSecs}` : currentSecs;

  // Current datetime
  // String such as 2016-07-16T19:20:30
  const zDateTime = `${currentYear}-${currentMonth}-${currentDate}T${currentHrs}:${currentMins}:${currentSecs}`;

  // Concatenate datetime and timezone offset to get an ISO-8601 datetime string.
  return zDateTime + timezoneStandard;
}

/**
 * Convert the first character to upper case.
 *
 * Source: https://github.com/vuejs/vue/blob/1.0/src/filters/index.js#L37
 */
export function capitalize(value) {
  if (!value && value !== 0) {
    return "";
  }

  return value.toString().charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Format the given money value.
 *
 * Source: https://github.com/vuejs/vue/blob/1.0/src/filters/index.js#L70
 */
export function currency(value, symbol = "$", addComma = true) {
  let valueVar = parseFloat(value);
  let head;
  if (!Number.isFinite(valueVar) || (!valueVar && valueVar !== 0)) {
    return "";
  }
  // We devide by 100 since our prices tables is holding integers only
  valueVar /= 100;
  const stringified = Math.abs(valueVar).toFixed(2);
  const intVal = stringified.slice(0, -1 - 2);
  const i = intVal.length % 3;
  if (addComma) {
    head = i > 0 ? intVal.slice(0, i) + (intVal.length > 3 ? "," : "") : "";
  } else {
    head = i > 0 ? intVal.slice(0, i) + (intVal.length > 3 ? "" : "") : "";
  }
  const floatVal = stringified.slice(-1 - 2);
  const sign = valueVar < 0 ? "-" : "";
  const currencySymbol = symbol;

  return sign + currencySymbol + head + intVal.slice(i).replace(/(\d{3})(?=\d)/g, "$1,") + floatVal;
}

// Outstanding balance on an order, in integer cents: total less payments, write-offs, credits, and trades.
export function orderRemainingBalance(order) {
  return (order.total || 0) - (order.total_payments || 0) - (order.total_write_offs || 0) - (order.total_credits || 0) - (order.total_trades || 0);
}

export function currencyInput(value) {
  const valueVar = parseFloat(value);
  if (!Number.isFinite(valueVar) || (!valueVar && valueVar !== 0)) {
    return "";
  }

  const val = (valueVar / 1).toFixed().replace(/,/g, "");
  return (val / 100).toFixed(2);
}

/**
 * Enabled/disabled tooltip message.
 */
export function tooltipToggle(value, enabledMessage, disabledMessage) {
  if (value == true) {
    return enabledMessage;
  }
  return disabledMessage;
}

/**
 * THC Limit filter.
 */
export function hotOrNot(value, category) {
  if (category == "Merchandise") {
    return "N/A";
  }
  if (value) {
    return "Over Limit";
  }
  return "Under Limit";
}

/**
 * Return CBD min or range
 */
export function cdbCount(min, max, unit, category) {
  let value = min;
  let maxValue = max;
  if (category == "Merchandise") {
    return "N/A";
  }
  if (!min) {
    return "Not Entered";
  }
  if (unit) {
    value += unit;
    maxValue += unit;
  }
  if (max) {
    value = `${value} - ${maxValue}`;
  }

  return value;
}

/**
 * Return yes or no
 */
export function yesOrNo(value) {
  if (value === true) {
    return "Yes";
  }
  if (value === false) {
    return "No";
  }
  if (typeof value != "undefined") {
    return "";
  }
  return null;
}

/**
 * Return yes or no
 */
export function yesOrNoInverse(value) {
  if (value === true) {
    return "No";
  }
  if (value === false) {
    return "Yes";
  }
  if (typeof value != "undefined") {
    return "";
  }
  return null;
}

/**
 * Add commas to numbers
 */
export function numberCommas(value) {
  let valueParsed;
  if (value) {
    valueParsed = parseInt(value, 10);
  } else {
    return null;
  }
  if (!valueParsed) {
    return null;
  }
  if (valueParsed < 0) {
    valueParsed = 0;
  }
  return valueParsed.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Percentage Helpers
 */
export function percentage(value, symbol = "%") {
  return value + symbol;
}

/**
 * That pesky rounding bug
 */
export function percentageRounded(value, decimals) {
  let workingValue = 0;
  let workingDecimals = 0;
  if (value) {
    workingValue = value;
  }
  if (decimals) {
    workingDecimals = decimals;
  }
  workingValue *= 100;
  workingValue = Math.round(workingValue * 10 ** workingDecimals) / 10 ** workingDecimals;
  workingValue += "%";
  return workingValue;
}

/**
 * Tag Styling
 */
export function tagStyle(value) {
  let style = "";
  if (value.background_color) {
    style = `background: ${value.background_color};`;
  }
  if (value.color) {
    style += `color: ${value.color};`;
  }
  return style;
}

export function truncate(text, length, suffix) {
  if (text.length > length) {
    return text.substring(0, length) + suffix;
  }
  return text;
}

export function humanFileSize(value, si = true, precision = 1) {
  const thresh = si ? 1000 : 1024;
  let valueParam = value;
  if (Math.abs(valueParam) < thresh) {
    return `${valueParam} B`;
  }
  const units = si ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"] : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** precision;
  do {
    valueParam /= thresh;
    u += 1;
  } while (Math.round(Math.abs(valueParam) * r) / r >= thresh && u < units.length - 1);
  return `${valueParam.toFixed(precision)} ${units[u]}`;
}

/**
 * Filesize Helper
 */
export function getFilesize(value) {
  if (value) {
    return filesize(value);
  }
  return null;
}
